import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, Rocket, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function GettingStarted() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("gs_dismissed") === "1");

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/me/getting-started").then((r) => setData(r.data)).catch(() => {});
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user]);

  if (!user || dismissed || !data) return null;
  const allDone = data.completed >= data.total;
  if (allDone) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div className="rounded-2xl border border-[#E6B34A]/25 bg-[#141C42] p-5 mb-8 fade-up" data-testid="getting-started">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-[#E6B34A]/12 flex items-center justify-center shrink-0"><Rocket className="w-4 h-4 text-[#E6B34A]" /></div>
          <div>
            <h3 className="font-display font-extrabold text-lg tracking-tight leading-none">Getting started</h3>
            <p className="text-xs text-zinc-400 mt-1" data-testid="gs-progress">{data.completed} of {data.total} done</p>
          </div>
        </div>
        <button onClick={() => { localStorage.setItem("gs_dismissed", "1"); setDismissed(true); }} data-testid="gs-dismiss" className="text-zinc-500 hover:text-white transition-colors" title="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-1.5 rounded-full bg-white/10 mt-4 overflow-hidden">
        <div className="h-full rounded-full bg-[#E6B34A] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ul className="mt-4 grid sm:grid-cols-2 gap-2">
        {data.items.map((it) => (
          <li key={it.key}>
            <Link
              to={it.link}
              data-testid={`gs-item-${it.key}`}
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors ${it.done ? "bg-white/[0.03]" : "bg-white/[0.03] hover:bg-white/[0.07]"}`}
            >
              {it.done
                ? <CheckCircle2 className="w-5 h-5 text-[#E6B34A] shrink-0" />
                : <Circle className="w-5 h-5 text-zinc-600 shrink-0" />}
              <span className={`text-sm flex-1 ${it.done ? "text-zinc-500 line-through" : "text-zinc-200 font-semibold"}`}>{it.label}</span>
              {!it.done && <ArrowRight className="w-4 h-4 text-zinc-500" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
